import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, ChevronUp, ChevronDown, HardHat, Mic, NotepadText, Download, MoreVertical } from "lucide-react";
import bookingApi from "../../api/booking";
import { openPaymentCheckout } from "../../utils/paymentUtils";
import BookingLogModal from "./BookingLogModal";
import DisputeModal from "../dispute/DisputeModal";
import CancelWarningModal from "./CancelWarningModal";
import HostBillingModal from "./HostBillingModal";
import BookingReviewModal from "../Booking/BookingReviewModal";
import { getCustomerBilling } from "../../api/profile";
import pendingApprovalIcon from "../../assets/booking status/pending approval.svg";
import { useToast } from "../../context/ToastContext";



const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const getStatusConfig = ({
  lifecycleStatus = "NORMAL",
  fulfillmentStatus = "NOT_STARTED",
  bookingStatus = "PENDING_PAYMENT",
  remainingAmount = 0,
  isProfessional = false,
}) => {
  const normLifecycle = String(lifecycleStatus || "NORMAL").toUpperCase();
  const normFulfillment = String(fulfillmentStatus || "NOT_STARTED").toUpperCase();
  const normBooking = String(bookingStatus || "PENDING_PAYMENT").toUpperCase();

  // 1. Highest Priority: Disputed / Cancelled
  if (normLifecycle === "DISPUTED" || normBooking === "DISPUTED") {
    return {
      key: "disputed",
      label: "Disputed",
      isDisputed: true,
      isCancelled: false,
      isCompleted: false,
      isPendingApproval: false,
      isInProgress: false,
      isConfirmed: false,
      textColor: "text-[#FF4C18]",
      borderColor: "border-[#FF4C18]",
      bgColor: "bg-white",
      icon: (
        <div className="w-5 h-5 rounded-full bg-[#FF4C18] flex items-center justify-center text-white shrink-0">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      ),
    };
  }

  if (normLifecycle === "CANCELLED") {
    return {
      key: "cancelled",
      label: "Cancelled",
      isDisputed: false,
      isCancelled: true,
      isCompleted: false,
      isPendingApproval: false,
      isInProgress: false,
      isConfirmed: false,
      textColor: "text-[#FF4C18]",
      borderColor: "border-[#FF4C18]",
      bgColor: "bg-white",
      icon: (
        <div className="w-5 h-5 rounded-full bg-[#FF4C18] flex items-center justify-center text-white shrink-0">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      ),
    };
  }

  // 2. Completed: Once bookingStatus is COMPLETED
  if (normBooking === "COMPLETED") {
    return {
      key: "completed",
      label: "Completed",
      isDisputed: false,
      isCancelled: false,
      isCompleted: true,
      isPendingApproval: false,
      isInProgress: false,
      isConfirmed: false,
      textColor: "text-[#489C65]",
      borderColor: "border-[#489C65]",
      bgColor: "bg-white",
      icon: (
        <div className="w-5 h-5 rounded-full bg-[#489C65] flex items-center justify-center text-white shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      ),
    };
  }

  // 3. Customer: If payment is not zero (pay amount remaining > 0) -> In Progress
  if (!isProfessional && remainingAmount > 0) {
    return {
      key: "in_progress",
      label: "In Progress",
      isDisputed: false,
      isCancelled: false,
      isCompleted: false,
      isPendingApproval: false,
      isInProgress: true,
      isConfirmed: false,
      textColor: "text-[#F7D200]",
      borderColor: "border-[#F7D200]",
      bgColor: "bg-white",
      icon: (
        <div className="w-5 h-5 rounded-full bg-[#F7D200] flex items-center justify-center text-white shrink-0">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
            <circle cx="12" cy="12" r="10" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
          </svg>
        </div>
      ),
    };
  }

  // 4. Fulfillment completed / pending customer approval -> Pending Approval
  if (normFulfillment === "COMPLETED" || normBooking === "PENDING_APPROVAL") {
    return {
      key: "pending_approval",
      label: "Pending Approval",
      isDisputed: false,
      isCancelled: false,
      isCompleted: false,
      isPendingApproval: true,
      isInProgress: false,
      isConfirmed: false,
      textColor: "text-[#4285F4]",
      borderColor: "border-[#4285F4]",
      bgColor: "bg-white",
      icon: (
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <img src={pendingApprovalIcon} alt="Pending Approval" className="w-5 h-5 object-contain" />
        </div>
      ),
    };
  }

  // 5. Default / Active Confirmed
  return {
    key: "confirmed",
    label: "Confirmed",
    isDisputed: false,
    isCancelled: false,
    isCompleted: false,
    isPendingApproval: false,
    isInProgress: false,
    isConfirmed: true,
    textColor: "text-[#489C65]",
    borderColor: "border-[#489C65]",
    bgColor: "bg-white",
    icon: (
      <div className="w-5 h-5 rounded-full bg-[#489C65] flex items-center justify-center text-white shrink-0">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
    ),
  };
};

const getRoleIcon = (role) => {
  const r = String(role || "").toLowerCase();
  if (r === "crew") return HardHat;
  if (r === "performer") return Mic;
  return NotepadText;
};

const formatPlanDisplay = (plan) => {
  const p = String(plan || "").toUpperCase();
  if (p === "FLEXIBLE" || p === "SECURE") return "Secure";
  if (p === "MODERATE") return "Moderate";
  if (p === "DIRECT") return "Flexible";
  return capitalizeFirstLetter(plan);
};

export default function BookingDetailsCard({ overview, isProfessional, proposals = [], loading, eventId, eventData }) {
  const { showSuccess, showError, showWarning } = useToast();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState("");
  const [retryInstallmentData, setRetryInstallmentData] = useState(null);
  const [isRetryingInstallment, setIsRetryingInstallment] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showBookingLog, setShowBookingLog] = useState(false);
  const [isPlatformChargesOpen, setIsPlatformChargesOpen] = useState(true);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputeModalView, setDisputeModalView] = useState("raise");
  const [disputeModalMode, setDisputeModalMode] = useState("standard");
  const [cancelWarningOpen, setCancelWarningOpen] = useState(false);
  const [selectedCancelBookingId, setSelectedCancelBookingId] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [multiMenuOpen, setMultiMenuOpen] = useState(false);
  const multiMenuRef = useRef(null);
  const [isDownloadingMultiInvoices, setIsDownloadingMultiInvoices] = useState(false);
  const [pendingBillingAction, setPendingBillingAction] = useState({ type: "statement", bookingId: null });
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const profCardRef = useRef(null);
  const [profCardMinHeight, setProfCardMinHeight] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
      if (multiMenuRef.current && !multiMenuRef.current.contains(event.target)) {
        setMultiMenuOpen(false);
      }
    };
    if (menuOpen || multiMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen, multiMenuOpen]);

  const handleDownloadSingleHostInvoice = async (bId) => {
    if (!bId) {
      showError("Booking ID not found.");
      return;
    }

    try {
      setDownloadingInvoice(true);
      const billingRes = await getCustomerBilling();
      const bDetails = billingRes?.billingDetails;
      setHostBillingDetails(bDetails);

      if (isBillingComplete(bDetails)) {
        const res = await bookingApi.downloadHostInvoicePdf(bId);
        let filename = `Invoice_${bId}.pdf`;
        const disposition = res.headers?.["content-disposition"];
        if (disposition && disposition.includes("filename=")) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            filename = match[1];
          }
        }
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        showSuccess("Invoice downloaded successfully!");
      } else {
        setPendingBillingAction({ type: "invoice", bookingId: bId });
        setHostBillingModalOpen(true);
      }
    } catch (err) {
      console.error("Single host invoice download error:", err);
      let errMsg = "Failed to download invoice";
      let errCode = null;
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errCode = parsed.code;
          errMsg = parsed.message || errMsg;
        } catch (_) {}
      } else if (err.response?.data) {
        errCode = err.response.data.code;
        errMsg = err.response.data.message || errMsg;
      }
      if (errCode === "PROFESSIONAL_KYC_REQUIRED") {
        showError("Professional haven't submitted their address yet, the download invoice is available once they submit it.");
        return;
      }
      showError(errMsg);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleDownloadInvoice = async (bId) => {
    if (!bId) {
      showError("Booking ID not found.");
      return;
    }

    try {
      setDownloadingInvoice(true);
      const res = await bookingApi.downloadPayoutInvoicePdf(bId);

      let filename = `GoPratle_Payout_Invoice_${bId}.pdf`;
      const disposition = res.headers?.["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("Invoice downloaded successfully!");
    } catch (err) {
      console.error("Failed to download invoice:", err);
      let errMsg = "Failed to download invoice. Please try again.";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errMsg = parsed.message || errMsg;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      showError(errMsg);
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const [isDownloadingStatement, setIsDownloadingStatement] = useState(false);
  const [hostBillingModalOpen, setHostBillingModalOpen] = useState(false);
  const [hostBillingDetails, setHostBillingDetails] = useState(null);

  const isBillingComplete = (details) => {
    return Boolean(
      details &&
      details.billingName &&
      details.billingName.trim().length >= 2 &&
      details.address &&
      details.address.trim().length >= 5 &&
      details.state &&
      details.state.trim().length > 0 &&
      details.pincode &&
      /^[1-9][0-9]{5}$/.test(details.pincode.trim())
    );
  };

  const executeStatementDownload = async () => {
    try {
      setIsDownloadingStatement(true);
      const res = await bookingApi.downloadCustomerStatementPdf(eventId);
      let filename = `GoPratle_Event_Summary_${eventId}.pdf`;
      const disposition = res.headers?.["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess("Event financial summary downloaded successfully!");
    } catch (err) {
      console.error("Statement download error:", err);
      let errMsg = "Failed to download summary statement";
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errMsg = parsed.message || errMsg;
        } catch (_) {}
      } else if (err.response?.data?.message) {
        errMsg = err.response.data.message;
      }
      showError(errMsg);
    } finally {
      setIsDownloadingStatement(false);
    }
  };

  const eligibleMultiInvoiceBookings = (overview?.bookings || []).filter(
    (b) =>
      b.bookingStatus === "COMPLETED" &&
      (b.lifecycleStatus || "NORMAL") === "NORMAL" &&
      String(b.paymentPlan || "").toUpperCase() !== "DIRECT"
  );

  const executeMultiInvoicesDownload = async () => {
    if (eligibleMultiInvoiceBookings.length === 0) {
      showError("Invoices are available once at least one booking is Completed.");
      return;
    }

    try {
      setIsDownloadingMultiInvoices(true);
      let successCount = 0;
      let kycPendingCount = 0;

      for (let i = 0; i < eligibleMultiInvoiceBookings.length; i++) {
        const b = eligibleMultiInvoiceBookings[i];
        const bId = b.bookingId || b._id;
        try {
          const res = await bookingApi.downloadHostInvoicePdf(bId);
          let filename = `Invoice_${bId}.pdf`;
          const disposition = res.headers?.["content-disposition"];
          if (disposition && disposition.includes("filename=")) {
            const match = disposition.match(/filename="?([^"]+)"?/);
            if (match && match[1]) {
              filename = match[1];
            }
          }
          const blob = new Blob([res.data], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
          successCount++;
        } catch (err) {
          console.error(`Failed to download invoice for booking ${bId}:`, err);
          let errCode = null;
          if (err.response?.data instanceof Blob) {
            try {
              const text = await err.response.data.text();
              const parsed = JSON.parse(text);
              errCode = parsed.code;
            } catch (_) {}
          } else if (err.response?.data) {
            errCode = err.response.data.code;
          }
          if (errCode === "PROFESSIONAL_KYC_REQUIRED") {
            kycPendingCount++;
          }
        }
        if (i < eligibleMultiInvoiceBookings.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }

      if (kycPendingCount > 0) {
        showError("Some professionals haven't submitted their KYC yet; once submitted, their invoices will also be available for download.");
      }

      if (successCount > 0) {
        showSuccess(`Downloaded ${successCount} invoice${successCount > 1 ? "s" : ""} successfully!`);
      } else if (kycPendingCount === 0) {
        showError("Failed to download invoices");
      }
    } catch (err) {
      console.error("Bulk invoices download error:", err);
      showError("Failed to download invoices");
    } finally {
      setIsDownloadingMultiInvoices(false);
    }
  };

  const handleDownloadMultiInvoicesClick = async () => {
    if (isDownloadingMultiInvoices) return;

    if (eligibleMultiInvoiceBookings.length === 0) {
      showError("Invoices are available once at least one booking is Completed.");
      return;
    }

    try {
      setIsDownloadingMultiInvoices(true);
      const billingRes = await getCustomerBilling();
      const bDetails = billingRes?.billingDetails;
      setHostBillingDetails(bDetails);

      if (isBillingComplete(bDetails)) {
        await executeMultiInvoicesDownload();
      } else {
        setIsDownloadingMultiInvoices(false);
        setPendingBillingAction({ type: "multi-invoice", bookingId: null });
        setHostBillingModalOpen(true);
      }
    } catch (err) {
      console.error("Error checking billing details:", err);
      setIsDownloadingMultiInvoices(false);
      setPendingBillingAction({ type: "multi-invoice", bookingId: null });
      setHostBillingModalOpen(true);
    }
  };

  const handleDownloadStatementClick = async () => {
    if (isDownloadingStatement) return;
    setPendingBillingAction({ type: "statement", bookingId: null });

    try {
      setIsDownloadingStatement(true);
      const billingRes = await getCustomerBilling();
      const bDetails = billingRes?.billingDetails;
      setHostBillingDetails(bDetails);

      if (isBillingComplete(bDetails)) {
        await executeStatementDownload();
      } else {
        setIsDownloadingStatement(false);
        setHostBillingModalOpen(true);
      }
    } catch (err) {
      console.error("Error checking billing details:", err);
      setIsDownloadingStatement(false);
      setHostBillingModalOpen(true);
    }
  };

  useEffect(() => {
    if (!isProfessional || loading || !profCardRef.current) return;
    if (isPlatformChargesOpen) {
      const timer = setTimeout(() => {
        if (profCardRef.current) {
          const h = profCardRef.current.offsetHeight;
          if (h > 0) {
            setProfCardMinHeight((prev) => Math.max(prev || 0, h));
          }
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isProfessional, loading, overview, isPlatformChargesOpen]);

  const eventDateRaw =
    eventData?.startDate ||
    eventData?.date ||
    eventData?.eventDate ||
    overview?.eventDate ||
    overview?.booking?.eventId?.startDate ||
    overview?.booking?.eventDate ||
    null;

  const eventDateObj = eventDateRaw ? new Date(eventDateRaw) : null;
  const isBeforeEventDate = eventDateObj ? eventDateObj.getTime() >= Date.now() : true;

  const handleInitiateCancel = (bId) => {
    setSelectedCancelBookingId(bId);
    setCancelWarningOpen(true);
  };

  const handleConfirmCancelWarning = () => {
    setCancelWarningOpen(false);
    setDisputeModalMode("cancellation");
    setDisputeModalView("raise");
    setDisputeModalOpen(true);
  };

  const handleOpenDispute = (bId, initialView = "raise") => {
    setSelectedCancelBookingId(bId);
    setDisputeModalMode("standard");
    setDisputeModalView(initialView);
    setDisputeModalOpen(true);
  };

  const handleProfessionalComplete = async (bId) => {
    if (!bId) {
      showError("Booking ID not found.");
      return;
    }
    try {
      setActionLoading(true);
      await bookingApi.markProfessionalComplete(bId);
      showSuccess("Service marked as completed! Customer can now review and confirm.");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      showError(err.response?.data?.message || err.message || "Failed to mark service complete");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCustomerComplete = async (bId) => {
    if (!bId) {
      showError("Booking ID not found.");
      return;
    }

    try {
      setActionLoading(true);
      await bookingApi.markCustomerComplete(bId);
      showSuccess("Booking confirmed and completed! Payout has been released.");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      showError(err.response?.data?.message || err.message || "Failed to complete booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayInstallment = async () => {
    if (paying || isRetryingInstallment) return;
    const paymentGroupId = overview?.paymentGroupId;
    const nextInst = overview?.paymentSummary?.nextUpcomingInstallment;
    const installmentIndex = nextInst?.index;

    if (!paymentGroupId || installmentIndex === undefined) {
      showWarning("Payment group or installment info not available. Please refresh.");
      return;
    }

    try {
      setPaying(true);
      setPayError("");

      // 1. Call backend to initiate installment payment (no provider parameter sent)
      const res = await bookingApi.payInstallment({
        paymentGroupId,
        installmentIndex,
      });

      if (res.data?.success) {
        const { gatewayOrder } = res.data.data;
        if (!gatewayOrder) {
          showError("Error: Gateway order info not returned from backend.");
          setPaying(false);
          return;
        }

        const handleReset = () => {
          setPaying(false);
        };

        // 2. Open payment SDK dynamically based on gatewayOrder.provider returned by backend
        await openPaymentCheckout(gatewayOrder, handleReset, handleReset);
      } else {
        setPayError(res.data?.message || "Failed to initiate installment payment.");
        setPaying(false);
      }
    } catch (err) {
      console.error("Installment payment error:", err);
      setPayError(err.response?.data?.message || err.message || "Payment failed. Please try again.");
      // Capture retryData if backend provides it (paymentGroupId + installmentIndex)
      const errRetryData = err.response?.data?.retryData;
      if (errRetryData) {
        setRetryInstallmentData(errRetryData);
      }
      setPaying(false);
    }
  };

  const handleRetryInstallment = async () => {
    if (paying || isRetryingInstallment) return;
    if (!retryInstallmentData) return;
    try {
      setIsRetryingInstallment(true);
      setPayError("");
      const res = await bookingApi.retryPayment(retryInstallmentData);
      if (res.data?.success) {
        const { gatewayOrder } = res.data.data;
        if (!gatewayOrder) {
          showError("Error: Gateway order not returned from backend.");
          setIsRetryingInstallment(false);
          return;
        }

        const handleReset = () => {
          setIsRetryingInstallment(false);
        };

        setRetryInstallmentData(null);
        await openPaymentCheckout(gatewayOrder, handleReset, handleReset);
      } else {
        setPayError(res.data?.message || "Failed to retry installment payment.");
        setIsRetryingInstallment(false);
      }
    } catch (err) {
      console.error("Retry installment error:", err);
      setPayError(err.response?.data?.message || err.message || "Retry failed. Please try again.");
      setIsRetryingInstallment(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[24px] border border-gray-155 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-5 w-full h-[320px] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#FF4D12] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  console.log("[DEBUG BookingDetailsCard overview]:", overview);

  // ==========================================
  // PROFESSIONAL VIEW
  // ==========================================
  if (isProfessional) {
    const referenceId = overview?.referenceEventId || overview?.booking?.referenceId || "TBD";
    const bookingId = overview?.booking?._id;
    const grossQuoteAmount = overview?.financialBreakdown?.grossQuoteAmount || overview?.financialBreakdown?.grandTotal || 0;
    const grandTotal = overview?.financialBreakdown?.grandTotal || grossQuoteAmount;
    const offPlatformAmount = overview?.financialBreakdown?.offPlatformAmount || 0;
    const directPayAmount = offPlatformAmount;
    const processedGoPratle = Math.max(0, grandTotal - offPlatformAmount);
    const platformFeePercent = overview?.financialBreakdown?.platformFeePercent || 0;
    const platformFeeAmount = overview?.financialBreakdown?.platformFeeAmount || 0;
    const taxPercent = overview?.financialBreakdown?.taxPercent || 18;
    const taxAmount = overview?.financialBreakdown?.taxAmount || 0;
    const taxConfig = overview?.financialBreakdown?.taxConfig || overview?.booking?.taxConfig || null;
    let taxLabel = `Taxes (${taxPercent}%)`;
    if (taxConfig?.cgstPercent > 0) {
      taxLabel = `Taxes (${taxConfig.cgstPercent}% CGST + ${taxConfig.sgstPercent}% SGST)`;
    } else if (taxConfig?.igstPercent > 0) {
      taxLabel = `Taxes (${taxConfig.igstPercent}% IGST)`;
    }
    const gatewayFeeAmount = overview?.financialBreakdown?.gatewayFeeAmount || 0;
    const totalPlatformCharges = platformFeeAmount + taxAmount + gatewayFeeAmount;
    const amountReleased = overview?.financialBreakdown?.releasedAmount || 0;

    // Booking plan from first installment or booking object
    const bookingPlan = overview?.installments?.[0]?.bookingOverviews?.[0]?.paymentPlan || overview?.booking?.paymentPlan || "Flexible";
    const hasFlexiblePlan = String(bookingPlan).toUpperCase() === "FLEXIBLE" || overview?.installments?.some(inst =>
      inst.bookingOverviews?.some(bo => String(bo.paymentPlan).toUpperCase() === "FLEXIBLE")
    );
    const milestonesCount = (overview?.installments?.filter(i => i.type === "FUTURE").length || 0) + 1;
    const bookingStatus = overview?.booking?.bookingStatus || "PENDING_PAYMENT";
    const lifecycleStatus = overview?.booking?.lifecycleStatus || "NORMAL";
    const fulfillmentStatus = overview?.booking?.fulfillmentStatus || "NOT_STARTED";

    const statusConfig = getStatusConfig({
      lifecycleStatus,
      fulfillmentStatus,
      bookingStatus,
      remainingAmount: 0,
      isProfessional: true,
    });

    const progressPercent = grossQuoteAmount > 0
      ? Math.min(100, Math.max(0, ((overview?.financialBreakdown?.paidByCustomerAmount || 0) / grossQuoteAmount) * 100))
      : 0;

    return (
      <>
        <div
          ref={profCardRef}
          style={profCardMinHeight ? { "--prof-card-min-h": `${profCardMinHeight}px` } : undefined}
          className="bg-white rounded-[18px] sm:rounded-[22px] lg:rounded-[24px] border border-gray-155 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-2.5 sm:p-3.5 lg:p-5 w-full h-auto lg:h-full lg:min-h-[var(--prof-card-min-h)] flex flex-col justify-between overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto min-h-0 pr-0.5 hide-scrollbar">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-100 pb-1 mb-1.5 lg:pb-2.5 lg:mb-3.5">
              <h3 className="font-bold text-gray-950 text-[13px] sm:text-[14px] lg:text-[16px]">Booking Details</h3>
              <span className="text-[9.5px] sm:text-[10.5px] lg:text-xs text-gray-400 font-semibold">
                Event ID: <span className="text-orange-500">#{overview?.referenceEventId || referenceId}</span>
              </span>
            </div>

            {/* Total Quoted Value */}
            <div className={`flex justify-between items-baseline ${hasFlexiblePlan ? "mb-1 lg:mb-2" : "mb-1.5 lg:mb-3"}`}>
              <span className="text-[10.5px] sm:text-[11.5px] lg:text-[13.5px] text-gray-600 font-medium">Total Quoted Value</span>
              <span className="text-[10.5px] sm:text-[11.5px] lg:text-[14px] font-medium text-gray-700">
                ₹{grossQuoteAmount.toLocaleString("en-IN")}
              </span>
            </div>

            {/* Progress Bar */}
            {hasFlexiblePlan && (
              <div className="w-full mb-1.5 lg:mb-3">
                <div className="w-full h-1 lg:h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Financial Rows */}
            <div className="space-y-1 sm:space-y-1.5 lg:space-y-3 mb-1.5 sm:mb-2 lg:mb-4 text-[10.5px] sm:text-[11.5px] lg:text-[13px]">
              <div className="flex justify-between items-center text-gray-600">
                <span className="font-medium">Total Processed via GoPratle</span>
                <span className="font-medium text-gray-700">₹{processedGoPratle.toLocaleString("en-IN")}</span>
              </div>

              {/* Platform Charges with Accordion */}
              <div>
                <div
                  className="flex justify-between items-center cursor-pointer select-none text-[#FF4D12]"
                  onClick={() => setIsPlatformChargesOpen(!isPlatformChargesOpen)}
                >
                  <span className="font-medium flex items-center gap-1">
                    Platform Charges
                    {isPlatformChargesOpen ? (
                      <ChevronUp className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-[#FF4D12]" />
                    ) : (
                      <ChevronDown className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-[#FF4D12]" />
                    )}
                  </span>
                  <span className="font-medium">
                    {isPlatformChargesOpen ? `- ₹${totalPlatformCharges.toLocaleString("en-IN")}` : `₹${totalPlatformCharges.toLocaleString("en-IN")}`}
                  </span>
                </div>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${isPlatformChargesOpen ? "max-h-40 opacity-100 mt-0.5 lg:mt-1" : "max-h-0 opacity-0 mt-0 pointer-events-none"
                    }`}
                >
                  <div className="pl-2 lg:pl-3 space-y-0.5 lg:space-y-1 text-[9.5px] sm:text-[10.5px] lg:text-xs text-gray-500 border-l border-gray-100 ml-1 py-0.5">
                    <div className="flex justify-between items-center">
                      <span>Platform Fee ({platformFeePercent}%)</span>
                      <span className="font-medium text-gray-600">₹{platformFeeAmount.toLocaleString("en-IN")}</span>
                    </div>
                    {Number(gatewayFeeAmount) > 0 && (
                      <div className="flex justify-between items-center">
                        <span>Process Fee (Estimate)</span>
                        <span className="font-medium text-gray-600">₹{gatewayFeeAmount.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                    {Number(taxAmount) > 0 && (
                      <div className="flex justify-between items-center">
                        <span>{taxLabel}</span>
                        <span className="font-medium text-gray-600">₹{taxAmount.toLocaleString("en-IN")}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {hasFlexiblePlan && (
                <div className="flex justify-between items-center text-gray-600">
                  <span className="font-medium">Milestones</span>
                  <span className="font-medium text-gray-700">{milestonesCount}</span>
                </div>
              )}

              <div className="flex justify-between items-center text-gray-600">
                <span className="font-medium">Settled Directly</span>
                <span className="font-medium text-gray-700">₹{directPayAmount.toLocaleString("en-IN")}</span>
              </div>

              <div className="flex justify-between items-center text-gray-600">
                <span className="font-medium">Amount Released</span>
                <span className="font-medium text-gray-700">₹{amountReleased.toLocaleString("en-IN")}</span>
              </div>

              <div className="flex justify-between items-center text-gray-600">
                <span className="font-medium">Booking Plan</span>
                <span className="font-medium text-gray-700">{formatPlanDisplay(bookingPlan)}</span>
              </div>
            </div>
          </div>

          <div className="mt-3.5 lg:mt-0 shrink-0">
            {/* Booking Status stuck at bottom */}
            <div className={`flex items-center gap-2 lg:gap-3 border rounded-[9px] lg:rounded-[12px] p-1.5 sm:p-2 lg:p-3 mb-1.5 lg:mb-2.5 ${statusConfig.borderColor} ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.icon}
              <div className="text-left">
                <span className="text-[8.5px] sm:text-[9.5px] lg:text-[11px] text-gray-400 font-bold block leading-tight">Booking Status</span>
                <span className="font-extrabold text-[12px] sm:text-[13px] lg:text-[15px] leading-tight">{statusConfig.label}</span>
              </div>
            </div>

            <div className="flex gap-1.5 lg:gap-2.5 mb-1 lg:mb-2">
              {statusConfig.isDisputed ? (
                <>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/help")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap"
                  >
                    Help
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenDispute(bookingId, "view")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 bg-[#FF4D12] text-white font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-[#e64415] transition shadow-sm flex items-center justify-center whitespace-nowrap"
                  >
                    View Dispute
                  </button>
                </>
              ) : statusConfig.isCancelled ? (
                <>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/help")}
                    className={`${
                      String(bookingPlan).toUpperCase() !== "DIRECT" && (overview?.booking?.refundStatus === "PARTIALLY_REFUNDED" || amountReleased > 0)
                        ? "w-20 sm:w-24 lg:w-28 shrink-0 px-2 sm:px-3"
                        : "w-full px-4"
                    } h-8 sm:h-8.5 lg:h-10 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap`}
                  >
                    Help
                  </button>
                  {String(bookingPlan).toUpperCase() !== "DIRECT" && (overview?.booking?.refundStatus === "PARTIALLY_REFUNDED" || amountReleased > 0) && (
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoice(bookingId)}
                      disabled={downloadingInvoice}
                      className="flex-1 min-w-0 h-8 sm:h-8.5 lg:h-10 px-3 sm:px-4 bg-[#FF4D12] text-white font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-[#e64415] active:scale-[0.99] transition shadow-sm flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                      {downloadingInvoice ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                          <span className="truncate">Downloading...</span>
                        </>
                      ) : (
                        <>
                          <Download size={14} className="shrink-0" />
                          <span className="truncate">Invoice</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : statusConfig.isCompleted ? (
                <>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/help")}
                    className={`${
                      String(bookingPlan).toUpperCase() !== "DIRECT"
                        ? "w-20 sm:w-24 lg:w-28 shrink-0 px-2 sm:px-3"
                        : "w-full px-4"
                    } h-8 sm:h-8.5 lg:h-10 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap`}
                  >
                    Help
                  </button>
                  {String(bookingPlan).toUpperCase() !== "DIRECT" && (
                    <button
                      type="button"
                      onClick={() => handleDownloadInvoice(bookingId)}
                      disabled={downloadingInvoice}
                      className="flex-1 min-w-0 h-8 sm:h-8.5 lg:h-10 px-3 sm:px-4 bg-[#FF4D12] text-white font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-[#e64415] active:scale-[0.99] transition shadow-sm flex items-center justify-center gap-1.5 sm:gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                      {downloadingInvoice ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                          <span className="truncate">Downloading...</span>
                        </>
                      ) : (
                        <>
                          <Download size={14} className="shrink-0" />
                          <span className="truncate">Invoice</span>
                        </>
                      )}
                    </button>
                  )}
                </>
              ) : statusConfig.isPendingApproval ? (
                <>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/help")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap"
                  >
                    Help
                  </button>
                  <button
                    type="button"
                    onClick={() => handleOpenDispute(bookingId, "raise")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap"
                  >
                    Raise Dispute
                  </button>
                </>
              ) : isBeforeEventDate ? (
                <>
                  <button
                    type="button"
                    onClick={() => (window.location.href = "/help")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap"
                  >
                    Help
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInitiateCancel(bookingId)}
                    disabled={actionLoading}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 bg-[#FF4D12] text-white font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-[#e64415] transition shadow-sm flex items-center justify-center disabled:opacity-60 whitespace-nowrap"
                  >
                    {actionLoading ? "Processing..." : "Cancel"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleOpenDispute(bookingId, "raise")}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 border border-orange-500 text-orange-500 font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap"
                  >
                    Raise Dispute
                  </button>
                  <button
                    type="button"
                    onClick={() => handleProfessionalComplete(bookingId)}
                    disabled={actionLoading}
                    className="flex-1 h-8 sm:h-8.5 lg:h-10 px-2 sm:px-3 bg-[#FF4D12] text-white font-bold rounded-[7px] lg:rounded-[8px] text-[11px] sm:text-[12px] lg:text-sm hover:bg-[#e64415] transition shadow-sm flex items-center justify-center disabled:opacity-60 whitespace-nowrap"
                  >
                    {actionLoading ? "Processing..." : "Mark Complete"}
                  </button>
                </>
              )}
            </div>

            <p className="text-[8.5px] sm:text-[9.5px] lg:text-[11px] text-gray-400 font-medium text-center leading-tight">
              Payout will be available after the event is completed.
            </p>
          </div>
        </div>

        <CancelWarningModal
          isOpen={cancelWarningOpen}
          onClose={() => setCancelWarningOpen(false)}
          onConfirm={handleConfirmCancelWarning}
        />

        <DisputeModal
          isOpen={disputeModalOpen}
          onClose={() => setDisputeModalOpen(false)}
          initialView={disputeModalView}
          mode={disputeModalMode}
          bookingId={selectedCancelBookingId || bookingId}
          bookingData={{
            bookingId: selectedCancelBookingId || bookingId,
            referenceId: overview?.referenceEventId || overview?.booking?.referenceId || "4004556",
            userName: overview?.customer?.fullName || "Customer",
            location: eventData?.location?.city || eventData?.location?.address || eventData?.city || "Delhi",
            createdAt: eventDateRaw,
          }}
          eventData={eventData || {}}
        />
      </>
    );
  }

  // ==========================================
  // CUSTOMER VIEW (Single & Multiple Bookings)
  // ==========================================
  const bookedProposals = !isProfessional
    ? proposals.filter((p) => {
        const isCancelled =
          p?.isCancelled ||
          p?.status === "cancelled" ||
          p?.status === "rejected" ||
          p?.status === "withdrawn" ||
          p?.lifecycleStatus === "CANCELLED";
        return !isCancelled && (p?.isBooked || p?.status === "booked" || Boolean(p?.bookingId));
      })
    : proposals;

  const effectiveProposals = bookedProposals.length > 0 ? bookedProposals : proposals;

  const isCustomerSingle =
    !isProfessional &&
    (overview?.booking != null ||
      (overview?.bookings && overview.bookings.length === 1) ||
      effectiveProposals.length === 1);

  const referenceId = overview?.referenceEventId || effectiveProposals[0]?.bookingId?.referenceId || overview?.paymentGroupId || "TBD";
  const totalQuotedValue = overview?.totalQuoteValue || overview?.paymentSummary?.totalGrandTotal || 0;

  // Has any flexible plan across installments' bookingOverviews
  const hasFlexiblePlan = overview?.installments?.some(inst =>
    inst.bookingOverviews?.some(bo => String(bo.paymentPlan).toUpperCase() === "FLEXIBLE")
  ) || effectiveProposals.some(p => String(p.paymentPlan || p.plan).toUpperCase() === "FLEXIBLE");

  const milestonesCount = (overview?.installments?.filter(i => i.type === "FUTURE").length || 0) + 1;

  // Total paid via GoPratle = sum of confirmed amounts in installments with status PAID
  const totalPaidViaGoPratle = overview?.installments
    ?.filter(inst => inst.status === "PAID")
    ?.reduce((sum, inst) => {
      const confirmedAmount = inst.bookingOverviews
        ?.filter(bo => bo.status === "confirmed" || bo.status === "CONFIRMED")
        ?.reduce((s, bo) => s + (bo.amount || 0), 0);
      return sum + (confirmedAmount != null ? confirmedAmount : (inst.totalAmount || 0));
    }, 0) || overview?.paymentSummary?.totalPaid || 0;

  // Promo Discount Applied
  const promoDiscount = Math.max(0, totalQuotedValue - (overview?.paymentSummary?.totalGrandTotal || totalQuotedValue));

  // Net Paid by You: paymentSummary.totalPaid
  const netPaidByYou = overview?.paymentSummary?.totalPaid ?? totalPaidViaGoPratle ?? 0;

  // Upcoming on Platform: The next unpaid/pending installment on platform
  const nextUnpaidInstallment = overview?.paymentSummary?.nextUpcomingInstallment
    ? overview?.installments?.find(
      inst => inst.index === overview.paymentSummary.nextUpcomingInstallment.index &&
        inst.status !== "PAID" &&
        inst.status !== "CANCELLED"
    ) || overview.paymentSummary.nextUpcomingInstallment
    : null;

  const onPlatformAmount = (nextUnpaidInstallment && nextUnpaidInstallment.type !== "OFF_PLATFORM")
    ? (nextUnpaidInstallment.bookingOverviews
      ?.filter(bo => String(bo.status).toUpperCase() === "CONFIRMED")
      ?.reduce((s, bo) => s + (bo.amount || 0), 0) ?? (nextUnpaidInstallment.amount ?? nextUnpaidInstallment.totalAmount ?? 0))
    : 0;

  // Direct Pay: Sum of confirmed booking overview amounts for all installments with type === 'OFF_PLATFORM'
  const directPaymentAmount = overview?.installments
    ?.filter(inst => inst.type === "OFF_PLATFORM" && inst.status !== "CANCELLED")
    ?.reduce((sum, inst) => {
      const confirmedAmount = inst.bookingOverviews
        ?.filter(bo => String(bo.status).toUpperCase() === "CONFIRMED")
        ?.reduce((s, bo) => s + (bo.amount || 0), 0);
      return sum + (confirmedAmount != null ? confirmedAmount : (inst.totalAmount || 0));
    }, 0) || overview?.paymentSummary?.offPlatformAmount || 0;

  // Upcoming Due Date: for installment type FUTURE and status != PAID
  const upcomingFutureInstallment = overview?.installments?.find(
    inst => String(inst.type).toUpperCase() === "FUTURE" &&
            String(inst.status).toUpperCase() !== "PAID" &&
            String(inst.status).toUpperCase() !== "CANCELLED" &&
            inst.dueDate
  ) || (
    String(nextUnpaidInstallment?.type).toUpperCase() === "FUTURE" &&
    String(nextUnpaidInstallment?.status).toUpperCase() !== "PAID" &&
    String(nextUnpaidInstallment?.status).toUpperCase() !== "CANCELLED" &&
    nextUnpaidInstallment?.dueDate
      ? nextUnpaidInstallment
      : null
  );

  const formatDueDate = (dateVal) => {
    if (!dateVal) return null;
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return null;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formattedDueDate = formatDueDate(upcomingFutureInstallment?.dueDate);

  // Booking Plan (for single proposal)
  const getSingleBookingPlan = () => {
    if (overview?.installments) {
      for (const inst of overview.installments) {
        if (inst.bookingOverviews && inst.bookingOverviews.length > 0) {
          const plan = inst.bookingOverviews[0].paymentPlan;
          if (plan) return plan;
        }
      }
    }
    return effectiveProposals[0]?.paymentPlan || effectiveProposals[0]?.plan || "Secure";
  };
  const singleBookingPlan = getSingleBookingPlan();

  const progressPercent = totalQuotedValue > 0
    ? Math.min(100, Math.max(0, (totalPaidViaGoPratle / totalQuotedValue) * 100))
    : 0;

  const canPay = Boolean(
    onPlatformAmount > 0 &&
    overview?.paymentSummary?.nextUpcomingInstallment &&
    overview?.paymentSummary?.nextUpcomingInstallment?.type !== "OFF_PLATFORM" &&
    (overview?.paymentSummary?.totalRemaining == null || overview.paymentSummary.totalRemaining > 0)
  );

  // Single Customer View
  if (isCustomerSingle) {
    const customerBooking = overview?.booking || (overview?.bookings && overview.bookings.length === 1 ? overview.bookings[0] : null);
    const custBookingId = customerBooking?._id || overview?.bookingId;
    const custBookingStatus = customerBooking?.bookingStatus || (overview?.paymentSummary?.totalRemaining === 0 ? "CONFIRMED" : "PARTIALLY_PAID");
    const custLifecycleStatus = customerBooking?.lifecycleStatus || "NORMAL";
    const custFulfillmentStatus = customerBooking?.fulfillmentStatus || "NOT_STARTED";
    const isReviewed = reviewSubmitted || Boolean(customerBooking?.hasReviewed);

    const statusConfig = getStatusConfig({
      lifecycleStatus: custLifecycleStatus,
      fulfillmentStatus: custFulfillmentStatus,
      bookingStatus: custBookingStatus,
      remainingAmount: onPlatformAmount,
      isProfessional: false,
    });

    return (
      <>
        <div className="bg-white rounded-[24px] border border-gray-155 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-4 sm:p-5 w-full h-full flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
              <h3 className="font-bold text-gray-950 text-[15px] sm:text-[16px]">Booking Details</h3>
              <div className="flex items-center gap-2">
                <span className="text-[11px] sm:text-xs text-gray-400 font-semibold">
                  Event ID: <span className="text-orange-500">#{referenceId}</span>
                </span>
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen((prev) => !prev)}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
                    title="More options"
                  >
                    <MoreVertical size={16} />
                  </button>
                  {menuOpen && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-30 text-xs text-gray-700">
                      {isBeforeEventDate && !statusConfig.isCancelled && !statusConfig.isCompleted && !statusConfig.isDisputed && (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            handleInitiateCancel(custBookingId);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium cursor-pointer"
                        >
                          Cancel Booking
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          window.location.href = "/help";
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium cursor-pointer"
                      >
                        Help
                      </button>
                      {statusConfig.isCompleted && (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            handleDownloadStatementClick();
                          }}
                          disabled={isDownloadingStatement}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium cursor-pointer disabled:opacity-50"
                        >
                          {isDownloadingStatement ? "Downloading..." : "Download Receipt"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={`flex justify-between items-baseline ${hasFlexiblePlan ? "mb-2" : "mb-4"}`}>
              <span className="text-xs sm:text-[13px] text-gray-700 font-medium">Total Quoted Value</span>
              <span className="text-xs sm:text-[13px] font-medium text-gray-700">
                ₹{totalQuotedValue.toLocaleString("en-IN")}
              </span>
            </div>

            {hasFlexiblePlan && (
              <div className="w-full mb-4">
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
            )}

            {/* Breakdown List */}
            <div className="space-y-2.5 mb-4 text-xs sm:text-[13px]">
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Total Processed via GoPratle</span>
                <span className="font-medium text-gray-700">₹{totalPaidViaGoPratle.toLocaleString("en-IN")}</span>
              </div>

              {hasFlexiblePlan && (
                <div className="flex justify-between items-center text-gray-700">
                  <span className="font-medium">Milestones</span>
                  <span className="font-medium text-gray-700">{milestonesCount}</span>
                </div>
              )}

              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Upcoming on Platform</span>
                <span className="font-medium text-gray-700">₹{onPlatformAmount.toLocaleString("en-IN")}</span>
              </div>

            {formattedDueDate && (
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Due Date</span>
                <span className="font-medium text-gray-700">{formattedDueDate}</span>
              </div>
            )}

              {directPaymentAmount > 0 && (
                <div className="flex justify-between items-center text-gray-700">
                  <span className="font-medium">Settled Directly</span>
                  <span className="font-medium text-gray-700">₹{directPaymentAmount.toLocaleString("en-IN")}</span>
                </div>
              )}

              {promoDiscount > 0 && (
                <div className="flex justify-between items-center text-red-500">
                  <span className="font-medium">Promo Discount Applied</span>
                  <span className="font-medium">-₹{promoDiscount.toLocaleString("en-IN")}</span>
                </div>
              )}

              <div className="flex justify-between items-center text-gray-700 pt-2 border-t border-gray-100">
                <span className="font-medium">Net Paid by You</span>
                <span className="font-medium text-gray-700">₹{netPaidByYou.toLocaleString("en-IN")}</span>
              </div>

              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Booking Plan</span>
                <span className="font-medium text-gray-700">{formatPlanDisplay(singleBookingPlan)}</span>
              </div>
            </div>
          </div>

          <div>
            <div className={`flex items-center gap-3 border rounded-[12px] p-3 mb-4 ${statusConfig.borderColor} ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.icon}
              <div className="text-left">
                <span className="text-[10px] text-gray-400 font-bold block leading-tight">Booking Status</span>
                <span className="font-extrabold text-sm sm:text-base leading-none">{statusConfig.label}</span>
              </div>
            </div>

            {payError && (
              <p className="text-[10px] text-red-500 font-medium text-center mb-1">{payError}</p>
            )}

            <div className="flex gap-2 mb-2">
              {!statusConfig.isCompleted ? (
                <>
                  <button
                    type="button"
                    onClick={handleDownloadStatementClick}
                    disabled={isDownloadingStatement}
                    className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {isDownloadingStatement ? (
                      <div className="flex items-center gap-1.5">
                        <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0" />
                        <span>Downloading...</span>
                      </div>
                    ) : (
                      "Receipt"
                    )}
                  </button>

                  {canPay ? (
                    <button
                      type="button"
                      onClick={handlePayInstallment}
                      disabled={paying || isRetryingInstallment}
                      className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center whitespace-nowrap"
                    >
                      {paying ? "Processing..." : `Pay now ₹${onPlatformAmount.toLocaleString("en-IN")}`}
                    </button>
                  ) : statusConfig.isPendingApproval ? (
                    <button
                      type="button"
                      onClick={() => handleCustomerComplete(custBookingId)}
                      disabled={actionLoading}
                      className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm flex items-center justify-center disabled:opacity-60 whitespace-nowrap"
                    >
                      {actionLoading ? "Processing..." : "Approve Work"}
                    </button>
                  ) : statusConfig.isDisputed ? (
                    <button
                      type="button"
                      onClick={() => handleOpenDispute(custBookingId, "view")}
                      className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm flex items-center justify-center whitespace-nowrap"
                    >
                      View Dispute
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/help")}
                      className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer"
                    >
                      Help
                    </button>
                  )}
                </>
              ) : (
                /* Completed booking CTAs: strictly 2 buttons, receipt shifted to menu */
                <>
                  {String(singleBookingPlan || "").toUpperCase() !== "DIRECT" ? (
                    <button
                      type="button"
                      onClick={() => handleDownloadSingleHostInvoice(custBookingId)}
                      disabled={downloadingInvoice}
                      className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm flex items-center justify-center gap-1.5 disabled:opacity-60 whitespace-nowrap cursor-pointer"
                    >
                      {downloadingInvoice ? (
                        <>
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin shrink-0" />
                          <span>Downloading...</span>
                        </>
                      ) : (
                        "Invoice"
                      )}
                    </button>
                  ) : null}

                  {!isReviewed ? (
                    <button
                      type="button"
                      onClick={() => setReviewModalOpen(true)}
                      className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm flex items-center justify-center whitespace-nowrap cursor-pointer"
                    >
                      Write Review
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/help")}
                      className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer"
                    >
                      Help
                    </button>
                  )}

                  {String(singleBookingPlan || "").toUpperCase() === "DIRECT" && !isReviewed && (
                    <button
                      type="button"
                      onClick={() => (window.location.href = "/help")}
                      className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer"
                    >
                      Help
                    </button>
                  )}
                </>
              )}
            </div>

            <p className="text-[10px] text-gray-400 font-medium text-center">
              Payouts will be released only after the event is completed.
            </p>
          </div>
        </div>

        <CancelWarningModal
          isOpen={cancelWarningOpen}
          onClose={() => setCancelWarningOpen(false)}
          onConfirm={handleConfirmCancelWarning}
        />

        <DisputeModal
          isOpen={disputeModalOpen}
          onClose={() => setDisputeModalOpen(false)}
          initialView={disputeModalView}
          mode={disputeModalMode}
          bookingId={selectedCancelBookingId || custBookingId}
          bookingData={{
            bookingId: selectedCancelBookingId || custBookingId,
            referenceId: overview?.referenceEventId || referenceId,
            userName: effectiveProposals[0]?.plannerName || effectiveProposals[0]?.companyName || "Professional",
            location: eventData?.location?.city || eventData?.location?.address || eventData?.city || "Delhi",
            createdAt: eventDateRaw,
          }}
          eventData={eventData || {}}
        />

        <HostBillingModal
          isOpen={hostBillingModalOpen}
          onClose={() => setHostBillingModalOpen(false)}
          initialBillingDetails={hostBillingDetails}
          onSuccess={async (updatedDetails) => {
            setHostBillingDetails(updatedDetails);
            setHostBillingModalOpen(false);
            if (pendingBillingAction.type === "invoice" && pendingBillingAction.bookingId) {
              await handleDownloadSingleHostInvoice(pendingBillingAction.bookingId);
            } else {
              await executeStatementDownload();
            }
          }}
        />

        <BookingReviewModal
          isOpen={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          bookingId={custBookingId}
          professionalId={effectiveProposals[0]?.professionalId?._id || effectiveProposals[0]?.professionalId || customerBooking?.professionalId}
          professionalName={effectiveProposals[0]?.plannerName || effectiveProposals[0]?.companyName || "Professional"}
          professionalImage={effectiveProposals[0]?.profilePicture}
          eventName={eventData?.eventName || overview?.referenceEventId || "Event"}
          onSuccess={() => setReviewSubmitted(true)}
        />
      </>
    );
  }

  // Multiple Customer View
  const getRoleLabel = () => {
    const rawRole = effectiveProposals[0]?.professionalRole || effectiveProposals[0]?.roleType || effectiveProposals[0]?.role || "Planner";
    const formatted = capitalizeFirstLetter(rawRole);
    const plural = formatted.endsWith("s") ? formatted : `${formatted}s`;
    const count = overview?.bookings?.length || effectiveProposals.length;
    return `${count} ${plural}`;
  };

  const RoleIcon = getRoleIcon(effectiveProposals[0]?.professionalRole || effectiveProposals[0]?.roleType || effectiveProposals[0]?.role);

  return (
    <>
      <div className="bg-white rounded-[24px] border border-gray-155 shadow-[0_2px_12px_rgba(0,0,0,0.03)] p-4 sm:p-5 w-full lg:h-full h-auto flex flex-col lg:justify-between justify-start gap-4 lg:gap-0">
        <div>
          <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-4">
            <h3 className="font-bold text-gray-900 text-[15px] sm:text-[16px]">Booking Details</h3>
            <div className="flex items-center gap-2">
              <span className="text-[10px] sm:text-[11px] text-gray-400 font-semibold">
                Event ID: <span className="text-orange-500">#{overview?.referenceEventId || referenceId}</span>
              </span>
              <div className="relative" ref={multiMenuRef}>
                <button
                  type="button"
                  onClick={() => setMultiMenuOpen((prev) => !prev)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition cursor-pointer"
                  title="More options"
                >
                  <MoreVertical size={16} />
                </button>
                {multiMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-30 text-xs text-gray-700">
                    <button
                      type="button"
                      disabled={isDownloadingMultiInvoices || eligibleMultiInvoiceBookings.length === 0}
                      onClick={() => {
                        setMultiMenuOpen(false);
                        handleDownloadMultiInvoicesClick();
                      }}
                      title={
                        eligibleMultiInvoiceBookings.length === 0
                          ? "Invoices available once at least one booking is Completed"
                          : "Download host tax invoices for completed bookings"
                      }
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isDownloadingMultiInvoices ? "Downloading..." : "Download Invoices"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setMultiMenuOpen(false);
                        window.location.href = "/help";
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-gray-700 font-medium cursor-pointer"
                    >
                      Help
                    </button>
                    
                  </div>
                )}
              </div>
            </div>
          </div>

          {hasFlexiblePlan && (
            <div className="w-full mb-4">
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}

          <div className="space-y-2.5 mb-4 text-xs sm:text-sm">
            <div className="flex justify-between items-center text-gray-700">
              <span className="font-medium">Total Quoted Value</span>
              <span className="font-medium text-gray-700">₹{totalQuotedValue.toLocaleString("en-IN")}</span>
            </div>

            {hasFlexiblePlan && (
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Milestones</span>
                <span className="font-medium text-gray-700">{milestonesCount}</span>
              </div>
            )}

            <div className="flex justify-between items-center text-gray-700">
              <span className="font-medium">Total Paid via GoPratle</span>
              <span className="font-medium text-gray-700">₹{totalPaidViaGoPratle.toLocaleString("en-IN")}</span>
            </div>

            <div className="flex justify-between items-center text-gray-700">
              <span className="font-medium">Upcoming on Platform</span>
              <span className="font-medium text-gray-700">₹{onPlatformAmount.toLocaleString("en-IN")}</span>
            </div>

          {formattedDueDate && (
            <div className="flex justify-between items-center text-gray-700">
              <span className="font-medium">Due Date</span>
              <span className="font-medium text-gray-700">{formattedDueDate}</span>
            </div>
          )}

            {directPaymentAmount > 0 && (
              <div className="flex justify-between items-center text-gray-700">
                <span className="font-medium">Settled Directly</span>
                <span className="font-medium text-gray-700">₹{directPaymentAmount.toLocaleString("en-IN")}</span>
              </div>
            )}

            {promoDiscount > 0 && (
              <div className="flex justify-between items-center text-red-500">
                <span className="font-medium">Promo Discount Applied</span>
                <span className="font-medium">-₹{promoDiscount.toLocaleString("en-IN")}</span>
              </div>
            )}

            <div className="flex justify-between items-center text-gray-700 pt-2 border-t border-gray-100">
              <span className="font-medium">Net Paid by You</span>
              <span className="font-medium text-gray-700">₹{netPaidByYou.toLocaleString("en-IN")}</span>
            </div>
          </div>
        </div>

        <div>
          <div
            className="flex items-center justify-between border border-[#FF5B26] bg-[#FFF8F5] rounded-[16px] px-4 py-3 mb-4 cursor-pointer hover:bg-[#FFF2EC] transition shadow-xs"
            onClick={() => setShowBookingLog(true)}
          >
            <div className="flex flex-col text-left">
              <h4 className="font-bold text-gray-950 text-[16px] sm:text-[17px] tracking-tight leading-tight">
                Professional Booked
              </h4>
              <div className="flex items-center gap-1.5 mt-1 text-xs sm:text-[13px] text-gray-600 font-medium">
                <RoleIcon className="w-4 h-4 text-gray-800 shrink-0" />
                <span>{getRoleLabel()}</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#FF5B26] stroke-[2.5] shrink-0" />
          </div>

          {payError && (
            <p className="text-[10px] text-red-500 font-medium text-center mb-1">{payError}</p>
          )}
          {retryInstallmentData && !paying && (
            <button
              type="button"
              onClick={handleRetryInstallment}
              disabled={paying || isRetryingInstallment}
              className="w-full h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center mb-2"
            >
              {isRetryingInstallment ? "Retrying..." : "Retry Payment"}
            </button>
          )}
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={handleDownloadStatementClick}
              disabled={isDownloadingStatement}
              className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer disabled:opacity-60"
            >
              {isDownloadingStatement ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-3.5 h-3.5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin shrink-0" />
                  <span>Downloading...</span>
                </div>
              ) : (
                "Receipt"
              )}
            </button>
            {canPay ? (
              <button
                type="button"
                onClick={handlePayInstallment}
                disabled={paying || isRetryingInstallment}
                className="flex-1 h-9 bg-[#FF4D12] text-white font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-[#e64415] transition shadow-sm disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {paying ? "Processing..." : `Pay now ₹${onPlatformAmount.toLocaleString("en-IN")}`}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => (window.location.href = "/help")}
                className="flex-1 h-9 border border-orange-500 text-orange-500 font-bold rounded-[8px] text-xs sm:text-[13px] hover:bg-orange-50 transition flex items-center justify-center whitespace-nowrap cursor-pointer"
              >
                Help
              </button>
            )}
          </div>

          <p className="text-[10px] text-gray-400 font-medium text-center">
            Payouts will be released only after the event is completed.
          </p>
        </div>
      </div>

      {/* Booking Log Modal */}
      <BookingLogModal
        isOpen={showBookingLog}
        onClose={() => setShowBookingLog(false)}
        eventId={eventId}
      />

      {/* Host Billing & Tax Modal */}
      <HostBillingModal
        isOpen={hostBillingModalOpen}
        onClose={() => setHostBillingModalOpen(false)}
        initialBillingDetails={hostBillingDetails}
        onSuccess={async (updatedDetails) => {
          setHostBillingDetails(updatedDetails);
          setHostBillingModalOpen(false);
          if (pendingBillingAction?.type === "multi-invoice") {
            await executeMultiInvoicesDownload();
          } else {
            await executeStatementDownload();
          }
        }}
      />
    </>
  );
}
